import clsx from 'clsx';
import { formatCompactCurrency, formatPercent } from '@/lib/format';

export type ProjectUseCategory = 'Land' | 'Hard' | 'Soft' | 'Financing' | 'Total';

export interface ProjectUseRow {
  category: ProjectUseCategory;
  amount: number;
  /** 0..1 — share of TDC. The `Total` row is expected to come in at 1.0
   *  (rendered as 100%); per-row checks happen in unit tests. */
  percentTDC: number;
  perUnit: number;
}

export interface ProjectUsesTableProps {
  uses: ProjectUseRow[];
  className?: string;
}

const CATEGORY_ORDER: readonly ProjectUseCategory[] = [
  'Land',
  'Hard',
  'Soft',
  'Financing',
  'Total',
] as const;

/**
 * 5-row × 3-column Project Uses table — the new-development variant's
 * replacement for the operating-asset Before/After Comparison row.
 *
 * Rows render in the canonical Land → Hard → Soft → Financing → Total
 * order regardless of the input array order. Missing categories render as
 * `—` rows so the table layout stays stable across schema-drift
 * conversations. The `Total` row gets a top-border treatment to set it
 * apart from the additive rows above.
 *
 * Empty state: when `uses` is empty (model schema lacks any of the 5
 * expected outputs), the table still renders 5 dash rows. This matches
 * the blueprint's "render gracefully" contract: never throw, never
 * collapse the layout.
 */
export function ProjectUsesTable({ uses, className }: ProjectUsesTableProps) {
  const byCategory = new Map<ProjectUseCategory, ProjectUseRow>();
  for (const row of uses) {
    byCategory.set(row.category, row);
  }

  return (
    <div
      data-testid="project-uses-table"
      data-row-count={uses.length}
      className={clsx('flex flex-col px-3 py-2', className)}
    >
      <div className="flex items-center gap-2.5 mb-1.5">
        <span
          aria-hidden="true"
          className="w-1 h-1 rounded-full bg-accent shrink-0"
          style={{ boxShadow: '0 0 4px rgba(0,200,83,0.6)' }}
        />
        <h3 className="text-[10px] font-mono uppercase tracking-[2px] text-accent font-bold shrink-0">
          Project Uses
        </h3>
        <span className="flex-1 h-px bg-panel-border" />
      </div>
      <div
        className="grid items-center border-b border-panel-border"
        style={{
          gridTemplateColumns: 'minmax(120px,1.4fr) 1fr 1fr 1fr',
          background: 'rgba(0, 200, 83, 0.024)',
        }}
      >
        <div className="px-3 py-1.5 text-[8.5px] font-mono text-text-muted uppercase tracking-[1px] font-semibold">
          Use
        </div>
        <div className="px-3 py-1.5 text-right text-[8.5px] font-mono text-text-muted uppercase tracking-[1px] font-semibold">
          Amount
        </div>
        <div className="px-3 py-1.5 text-right text-[8.5px] font-mono text-text-muted uppercase tracking-[1px] font-semibold">
          % of TDC
        </div>
        <div className="px-3 py-1.5 text-right text-[8.5px] font-mono text-text-muted uppercase tracking-[1px] font-semibold">
          Per Unit
        </div>
      </div>
      {CATEGORY_ORDER.map((category) => {
        const row = byCategory.get(category);
        const isTotal = category === 'Total';
        const hasData = row != null && Number.isFinite(row.amount);
        return (
          <div
            key={category}
            data-row-category={category}
            data-row-has-data={hasData ? 'true' : 'false'}
            className={clsx(
              'grid items-center border-b border-panel-border/40 last:border-b-0',
              isTotal && 'bg-accent-dim/30',
            )}
            style={{ gridTemplateColumns: 'minmax(120px,1.4fr) 1fr 1fr 1fr' }}
          >
            <div
              className={clsx(
                'px-3 py-1.5 text-[10.5px] font-mono uppercase tracking-[0.3px]',
                isTotal ? 'text-accent font-bold' : 'text-text',
              )}
            >
              {category === 'Hard' ? 'Hard Costs' : category === 'Soft' ? 'Soft Costs' : category === 'Financing' ? 'Financing Costs' : category === 'Total' ? 'Total Dev Cost' : 'Land Cost'}
            </div>
            <div
              data-cell="amount"
              className={clsx(
                'px-3 py-1.5 text-right text-[11px] font-mono tabular-nums',
                isTotal ? 'text-accent font-bold' : 'text-text-bright font-medium',
              )}
            >
              {hasData ? formatCompactCurrency(row.amount) : '—'}
            </div>
            <div
              data-cell="percentTDC"
              className={clsx(
                'px-3 py-1.5 text-right text-[11px] font-mono tabular-nums',
                isTotal ? 'text-accent font-bold' : 'text-text-muted',
              )}
            >
              {hasData && Number.isFinite(row.percentTDC)
                ? formatPercent(row.percentTDC, 1)
                : '—'}
            </div>
            <div
              data-cell="perUnit"
              className={clsx(
                'px-3 py-1.5 text-right text-[11px] font-mono tabular-nums',
                isTotal ? 'text-accent font-bold' : 'text-text-muted',
              )}
            >
              {hasData && Number.isFinite(row.perUnit)
                ? formatCompactCurrency(row.perUnit)
                : '—'}
            </div>
          </div>
        );
      })}
    </div>
  );
}
